'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

// ─── Shared styles (mirrors login page) ──────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  backgroundColor: 'var(--bg)',
  border: '1px solid var(--border-med)',
  borderRadius: '5px',
  color: 'var(--ink)',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 300,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: 'var(--ink-mute)',
  marginBottom: '8px',
};

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '11px 16px',
  backgroundColor: disabled ? 'var(--border-med)' : 'var(--clay)',
  border: '1px solid ' + (disabled ? 'var(--border-med)' : 'var(--clay)'),
  borderRadius: '5px',
  color: 'var(--ink)',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.2em',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background-color 0.15s ease',
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function generateWebhookSecret(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '28px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? '20px' : '6px',
            height: '6px',
            borderRadius: '3px',
            backgroundColor: i === current ? 'var(--clay)' : 'var(--border-med)',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </div>
  );
}

// ─── Animated checkmark ───────────────────────────────────────────────────────

function AnimatedCheckmark() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
      <svg
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
        style={{ animation: 'checkmark-pop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards' }}
      >
        <circle cx="28" cy="28" r="27" stroke="var(--clay)" strokeWidth="2" fill="rgba(139,105,20,0.08)" />
        <path
          d="M17 28.5L24 35.5L39 21"
          stroke="var(--clay)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ animation: 'checkmark-draw 0.4s 0.2s ease forwards', strokeDasharray: 30, strokeDashoffset: 30 }}
        />
      </svg>
      <style>{`
        @keyframes checkmark-pop {
          0% { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes checkmark-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

// ─── Slide wrapper ────────────────────────────────────────────────────────────

interface SlideProps {
  children: React.ReactNode;
  direction: 'enter' | 'idle';
}

function SlideIn({ children, direction }: SlideProps) {
  const [style, setStyle] = useState<React.CSSProperties>(
    direction === 'enter'
      ? { transform: 'translateX(40px)', opacity: 0 }
      : { transform: 'translateX(0)', opacity: 1 }
  );

  useEffect(() => {
    if (direction === 'enter') {
      const raf = requestAnimationFrame(() => {
        setStyle({ transform: 'translateX(0)', opacity: 1, transition: 'all 0.28s cubic-bezier(0.25,0.46,0.45,0.94)' });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [direction]);

  return <div style={style}>{children}</div>;
}

// ─── Main component ───────────────────────────────────────────────────────────

type Step = 0 | 1 | 2 | 3;

export default function SignupPage() {
  const router = useRouter();
  const supabaseRef = useRef(supabase);
  const supabaseClient = supabaseRef.current;

  const [step, setStep] = useState<Step>(0);
  const [animKey, setAnimKey] = useState(0);

  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Slug availability
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step loading/error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Slug availability check ──────────────────────────────────────────────

  const checkSlug = useCallback(
    async (value: string) => {
      if (!value || value.length < 2) {
        setSlugStatus('idle');
        return;
      }
      setSlugStatus('checking');
      const { data, error: qErr } = await supabaseClient
        .from('organizations')
        .select('id')
        .eq('slug', value)
        .maybeSingle();
      if (qErr) {
        setSlugStatus('idle');
        return;
      }
      setSlugStatus(data ? 'taken' : 'available');
    },
    []
  );

  // Auto-generate slug from company name
  useEffect(() => {
    if (!slugManuallyEdited) {
      const generated = toSlug(companyName);
      setSlug(generated);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => checkSlug(generated), 400);
    }
  }, [companyName, slugManuallyEdited, checkSlug]);

  // Manual slug edit
  const handleSlugChange = (value: string) => {
    const cleaned = toSlug(value);
    setSlug(cleaned);
    setSlugManuallyEdited(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => checkSlug(cleaned), 400);
  };

  // ── Navigation ───────────────────────────────────────────────────────────

  const goToStep = (next: Step) => {
    setError('');
    setStep(next);
    setAnimKey((k) => k + 1);
  };

  // ── Step handlers ────────────────────────────────────────────────────────

  const handleAccountContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    sessionStorage.setItem('signup_email', email.trim());
    goToStep(1);
  };

  const handleNameContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) return;
    sessionStorage.setItem('signup_first_name', firstName.trim());
    goToStep(2);
  };

  const handleCompanyContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !slug) return;
    if (slugStatus === 'taken') {
      setError('That slug is taken — please choose another.');
      return;
    }
    if (slugStatus === 'checking') {
      setError('Please wait while we check slug availability.');
      return;
    }

    sessionStorage.setItem('signup_company_name', companyName.trim());
    sessionStorage.setItem('signup_slug', slug);
    sessionStorage.setItem('signup_webhook_secret', generateWebhookSecret());

    setLoading(true);
    setError('');

    try {
      const webhookSecret = generateWebhookSecret();
      sessionStorage.setItem('signup_webhook_secret', webhookSecret);

      // Encode signup data into the redirect URL so it survives cross-tab navigation
      const redirectParams = new URLSearchParams({
        fn: firstName.trim(),
        cn: companyName.trim(),
        sl: slug,
        ws: webhookSecret,
      });
      const emailRedirectTo = `https://notesonthis.com/auth/callback?${redirectParams.toString()}`;

      const { error: signUpErr } = await supabaseClient.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo,
          data: {
            first_name: firstName.trim(),
            display_name: firstName.trim(),
            full_name: firstName.trim(),
          },
        },
      });

      if (signUpErr) {
        setError(signUpErr.message || 'Failed to create account. Please try again.');
        return;
      }

      goToStep(3);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Small helpers ────────────────────────────────────────────────────────────

  const slugHintStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 300,
    margin: '0 0 4px 0',
  };

  function ErrorMsg({ message }: { message: string }) {
    return (
      <div
        style={{
          padding: '10px 12px',
          backgroundColor: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '5px',
          color: '#ef4444',
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          fontWeight: 300,
          marginBottom: '16px',
          lineHeight: 1.5,
        }}
      >
        {message}
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <AppLogo size={40} />
          </div>
          <h1
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 200,
              fontSize: '24px',
              color: 'var(--ink)',
              letterSpacing: '-0.01em',
              marginBottom: '6px',
            }}
          >
            Notes On This
          </h1>
          <p
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.28em',
              color: 'var(--ink-mute)',
            }}
          >
            Create your workspace
          </p>
        </div>

        {/* Card */}
        <div
          style={{
            backgroundColor: 'var(--bg-soft)',
            border: '1px solid var(--border-med)',
            borderRadius: '8px',
            padding: '32px',
            marginBottom: '24px',
            overflow: 'hidden',
          }}
        >
          <StepDots current={step} total={4} />

          <SlideIn key={animKey} direction="enter">
            {/* ── STEP 0: Email + Password ── */}
            {step === 0 && (
              <form onSubmit={handleAccountContinue}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Work email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="you@company.com"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Min. 8 characters"
                      style={{ ...inputStyle, paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        color: 'var(--ink-mute)',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && <ErrorMsg message={error} />}

                <button type="submit" style={primaryBtn(!email.trim() || password.length < 8)}>
                  Continue
                </button>

                <p
                  style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    fontWeight: 300,
                    color: 'var(--ink-mute)',
                  }}
                >
                  Already have an account?{' '}
                  <Link
                    href="/login"
                    style={{ color: 'var(--clay)', textDecoration: 'none', fontWeight: 400 }}
                  >
                    Sign in
                  </Link>
                </p>
              </form>
            )}

            {/* ── STEP 1: Name ── */}
            {step === 1 && (
              <form onSubmit={handleNameContinue}>
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>What should we call you?</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoFocus
                    placeholder="First name"
                    style={inputStyle}
                  />
                </div>

                {error && <ErrorMsg message={error} />}

                <button type="submit" style={primaryBtn(!firstName.trim())}>
                  Continue
                </button>
              </form>
            )}

            {/* ── STEP 2: Company ── */}
            {step === 2 && (
              <form onSubmit={handleCompanyContinue}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={labelStyle}>What&apos;s your company called?</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    autoFocus
                    placeholder="Acme Inc."
                    style={inputStyle}
                  />
                </div>

                {/* Live portal preview */}
                {slug && (
                  <div
                    style={{
                      padding: '10px 12px',
                      backgroundColor: 'var(--bg)',
                      border: '1px solid var(--border-med)',
                      borderRadius: '5px',
                      marginBottom: '12px',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '11px',
                        color: 'var(--ink-mute)',
                        margin: 0,
                      }}
                    >
                      Your portal:{' '}
                      <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
                        notesonthis.com/portal/{slug}
                      </span>
                    </p>
                  </div>
                )}

                {/* Slug availability indicator */}
                {slug && (
                  <div style={{ marginBottom: '16px' }}>
                    {slugStatus === 'checking' && (
                      <p style={{ ...slugHintStyle, color: 'var(--ink-mute)' }}>Checking availability…</p>
                    )}
                    {slugStatus === 'available' && (
                      <p style={{ ...slugHintStyle, color: '#16a34a' }}>✓ Available</p>
                    )}
                    {slugStatus === 'taken' && (
                      <p style={{ ...slugHintStyle, color: '#ef4444' }}>
                        That name is taken — try another
                      </p>
                    )}
                  </div>
                )}

                {/* Manual slug edit */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ ...labelStyle, marginTop: '4px' }}>Edit your portal URL</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                    <span
                      style={{
                        padding: '10px 10px',
                        backgroundColor: 'var(--bg)',
                        border: '1px solid var(--border-med)',
                        borderRight: 'none',
                        borderRadius: '5px 0 0 5px',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '11px',
                        color: 'var(--ink-mute)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      /portal/
                    </span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="your-company"
                      style={{
                        ...inputStyle,
                        borderRadius: '0 5px 5px 0',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '12px',
                      }}
                    />
                  </div>
                </div>

                {error && <ErrorMsg message={error} />}

                <button
                  type="submit"
                  disabled={loading || !companyName.trim() || !slug || slugStatus === 'taken' || slugStatus === 'checking'}
                  style={primaryBtn(loading || !companyName.trim() || !slug || slugStatus === 'taken' || slugStatus === 'checking')}
                >
                  {loading ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            )}

            {/* ── STEP 3: Confirmation ── */}
            {step === 3 && (
              <div style={{ textAlign: 'center' }}>
                <AnimatedCheckmark />

                <h2
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 300,
                    fontSize: '22px',
                    color: 'var(--ink)',
                    marginBottom: '12px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Confirm your email
                </h2>

                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 300,
                    color: 'var(--ink-mute)',
                    lineHeight: 1.6,
                    marginBottom: '12px',
                  }}
                >
                  We&apos;ve sent a confirmation link to{' '}
                  <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>{email}</strong>.
                </p>

                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 300,
                    color: 'var(--ink-mute)',
                    lineHeight: 1.6,
                    marginBottom: '28px',
                  }}
                >
                  Click the link in that email to verify your account and start setting up your workspace with AI.
                </p>

                <button
                  onClick={() => goToStep(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    fontWeight: 300,
                    color: 'var(--ink-mute)',
                    padding: 0,
                  }}
                >
                  Wrong email? Start over
                </button>
              </div>
            )}
          </SlideIn>
        </div>
      </div>
    </div>
  );
}
